import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';
import { Settings, LayoutDashboard, Receipt, LogOut, ChevronDown, ShieldCheck, Users, Heart } from 'lucide-react';

export default function Header() {
  const { user, isAuthenticated, isAdmin, isAmbassador, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => { logout(); navigate('/login'); };

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    if (!profileMenuOpen) return;
    function handleOutsideClick(e: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setProfileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [profileMenuOpen]);

  const navLinks = [
    ...(!isAuthenticated ? [{ label: 'Home', path: '/' }] : []),
    { label: 'Projects', path: '/projects' },
    ...(isAuthenticated ? [
      { label: 'Dashboard', path: '/dashboard' },
      { label: 'Donations', path: '/donations/my-donations' },
      ...(isAmbassador ? [{ label: 'My Donors', path: '/ambassador' }] : []),
    ] : []),
    ...(isAdmin ? [{ label: 'Admin', path: '/admin' }] : []),
  ];

  const roleBadge = isAdmin
    ? { label: 'Admin', bg: '#da1e28', fg: '#ffffff' }
    : isAmbassador
    ? { label: 'Ambassador', bg: '#009d9a', fg: '#ffffff' }
    : { label: 'Donor', bg: '#e0e0e0', fg: '#525252' };

  return (
    <header
      className="sticky top-0 z-50 bg-white border-b border-carbon-20"
      style={scrolled ? { boxShadow: '0 1px 8px rgba(0,0,0,.07)' } : undefined}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">

          {/* ── Logo ─────────────────────────────────────────── */}
          <Link to="/" className="flex items-center gap-3 group shrink-0 min-w-0">
            <div className="w-8 h-8 bg-carbon-100 border border-carbon-90 flex items-center justify-center p-1.5 shrink-0 group-hover:bg-primary-500 transition-colors">
              <img src={logo} alt="Rahmani Foundation" className="w-full h-full object-contain" />
            </div>
            <div className="hidden sm:block min-w-0">
              <span className="block font-semibold text-carbon-100 text-[13px] leading-tight truncate">Rahmani Foundation</span>
              <span className="block text-[10px] font-normal text-carbon-40 uppercase tracking-[0.14em] leading-tight">Donation Platform</span>
            </div>
          </Link>

          {/* ── Desktop nav ──────────────────────────────────── */}
          <nav className="hidden md:flex items-center" aria-label="Main navigation">
            {loading ? (
              <div className="flex gap-1">
                {[60, 72, 56].map(w => (
                  <div key={w} className="skeleton h-8" style={{ width: w }} />
                ))}
              </div>
            ) : navLinks.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `relative px-4 py-5 text-[13px] font-medium transition-colors ${
                    isActive
                      ? 'text-primary-500 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary-500'
                      : 'text-carbon-60 hover:text-carbon-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          {/* ── Right side ───────────────────────────────────── */}
          <div className="hidden md:flex items-center gap-2">
            {loading ? (
              <div className="skeleton w-28 h-9" />
            ) : isAuthenticated ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setProfileMenuOpen(v => !v)}
                  aria-expanded={profileMenuOpen}
                  aria-haspopup="true"
                  className="flex items-center gap-2 px-2.5 py-1.5 border border-carbon-20 hover:border-carbon-30 hover:bg-carbon-10 transition-colors"
                >
                  {user?.image ? (
                    <img src={user.image} alt={user.name || 'User'} className="w-7 h-7 object-cover border border-carbon-20 shrink-0" style={{ borderRadius: 0 }} />
                  ) : (
                    <div className="w-7 h-7 bg-primary-500 flex items-center justify-center text-white font-semibold text-xs shrink-0">
                      {(user?.name || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="hidden lg:block text-left min-w-0">
                    <p className="text-xs font-semibold text-carbon-100 leading-tight truncate max-w-[110px]">{user?.name || 'User'}</p>
                    <p className="text-[10px] text-carbon-40 leading-tight">{user?.userCode || '—'}</p>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-carbon-40 transition-transform duration-200 ${profileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {profileMenuOpen && (
                  <div
                    className="absolute right-0 mt-1 w-56 bg-white border border-carbon-20 py-1 animate-slide-down z-50"
                    style={{ boxShadow: '0 8px 24px rgba(0,0,0,.10)' }}
                  >
                    {/* Profile header */}
                    <div className="px-4 py-3 border-b border-carbon-10 mb-1">
                      <p className="text-[13px] font-semibold text-carbon-100">{user?.name || 'User'}</p>
                      <p className="text-[11px] text-carbon-40 font-mono mt-0.5">{user?.userCode || '—'}</p>
                      <span
                        className="inline-block mt-1.5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                        style={{ background: roleBadge.bg, color: roleBadge.fg }}
                      >
                        {roleBadge.label}
                      </span>
                    </div>

                    {[
                      { label: 'Profile & KYC', path: '/profile', icon: Settings },
                      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
                      { label: 'My Donations', path: '/donations/my-donations', icon: Heart },
                      ...(isAmbassador ? [{ label: 'Ambassador Hub', path: '/ambassador', icon: ShieldCheck }] : []),
                      { label: 'Receipts', path: '/receipts', icon: Receipt },
                      ...(isAdmin ? [{ label: 'Admin Panel', path: '/admin', icon: Users }] : []),
                    ].map(link => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setProfileMenuOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 text-[13px] text-carbon-70 hover:text-carbon-100 hover:bg-carbon-10 transition-colors"
                      >
                        <link.icon className="w-3.5 h-3.5 text-carbon-40 shrink-0" />
                        {link.label}
                      </Link>
                    ))}

                    <div className="mx-3 my-1 border-t border-carbon-10" />
                    <button
                      onClick={() => { setProfileMenuOpen(false); handleLogout(); }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-[13px] text-red-500 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-3.5 h-3.5 shrink-0" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="btn btn-primary btn-sm">Sign in</Link>
            )}
          </div>

          {/* ── Mobile menu toggle ───────────────────────────── */}
          <button
            onClick={() => setMobileMenuOpen(v => !v)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            className="md:hidden p-2 text-carbon-70 hover:bg-carbon-10 border border-transparent hover:border-carbon-20 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>

        {/* ── Mobile menu ──────────────────────────────────────── */}
        {mobileMenuOpen && (
          <>
            <MobileMenuEscapeHandler onClose={() => setMobileMenuOpen(false)} />
            <div className="md:hidden border-t border-carbon-20 bg-white pb-4 pt-2 animate-slide-down">
              <nav className="flex flex-col">
                {loading ? (
                  <div className="h-10 skeleton mx-4 my-1" />
                ) : (
                  <>
                    {navLinks.map(item => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={item.path === '/'}
                        onClick={() => setMobileMenuOpen(false)}
                        className={({ isActive }) =>
                          `px-5 py-3 text-[13px] font-medium border-l-2 transition-colors ${
                            isActive
                              ? 'border-primary-500 text-primary-500 bg-primary-50'
                              : 'border-transparent text-carbon-70 hover:text-carbon-100 hover:bg-carbon-10'
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    ))}
                    <div className="mx-4 my-2 border-t border-carbon-20" />
                    {isAuthenticated ? (
                      <>
                        <div className="px-5 py-2 flex items-center gap-2">
                          <span className="text-xs font-medium text-carbon-40">Signed in as</span>
                          <span className="text-xs font-semibold text-carbon-100">{user?.name}</span>
                          <span
                            className="px-1.5 py-0.5 text-[10px] font-semibold uppercase"
                            style={{ background: roleBadge.bg, color: roleBadge.fg }}
                          >
                            {roleBadge.label}
                          </span>
                        </div>
                        <button
                          onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                          className="mx-4 mt-1 px-4 py-2.5 text-[13px] font-medium text-red-500 hover:bg-red-50 text-left border border-red-100 transition-colors"
                        >
                          Sign out
                        </button>
                      </>
                    ) : (
                      <div className="px-4 pt-1">
                        <Link
                          to="/login"
                          onClick={() => setMobileMenuOpen(false)}
                          className="btn btn-primary w-full justify-center"
                        >
                          Sign in
                        </Link>
                      </div>
                    )}
                  </>
                )}
              </nav>
            </div>
          </>
        )}
      </div>
    </header>
  );
}

function MobileMenuEscapeHandler({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return null;
}
